import Roles from '../config/Roles';

export const THEME_STORAGE_KEY = 'theme';
export const THEMES = Object.freeze({
    LIGHT: 'light',
    DARK: 'dark',
    SYSTEM: 'system',
});

const VALID_PREFERENCES = new Set([THEMES.LIGHT, THEMES.DARK, THEMES.SYSTEM]);

const DARK_MODE_ROLES = new Set([
    Roles.SUPER_ADMIN,
    Roles.GROUP_ADMIN,
    Roles.COMPANY_USER,
]);

/**
 * Dark mode is available to super_admin, group_admin, and company_user
 * (and on the login screen before a session exists).
 */
export function canUseDarkMode() {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) {
            // Login / public auth screens: allow choosing a preference.
            return true;
        }
        const user = JSON.parse(raw);
        const roles = user?.roles;
        if (!Array.isArray(roles)) {
            return false;
        }
        return roles.some((role) => {
            const name = typeof role === 'string' ? role : role?.name;
            return DARK_MODE_ROLES.has(name);
        });
    } catch {
        return false;
    }
}

export function getSystemTheme() {
    try {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return THEMES.DARK;
        }
    } catch {
        // ignore
    }
    return THEMES.LIGHT;
}

/**
 * Read persisted preference: light | dark | system.
 * Users without dark-mode access always resolve to light preference.
 */
export function getStoredPreference() {
    if (!canUseDarkMode()) {
        return THEMES.LIGHT;
    }
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        if (VALID_PREFERENCES.has(stored)) {
            return stored;
        }
    } catch {
        // localStorage may be unavailable
    }
    return THEMES.LIGHT;
}

/** @deprecated use getStoredPreference — kept for older call sites */
export function getStoredTheme() {
    return getStoredPreference();
}

export function resolveTheme(preference) {
    if (!canUseDarkMode()) {
        return THEMES.LIGHT;
    }
    if (preference === THEMES.SYSTEM) {
        return getSystemTheme();
    }
    return preference === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT;
}

/**
 * Apply resolved light/dark theme to <html>.
 */
export function applyTheme(resolved) {
    const mode = resolved === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT;
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    root.style.colorScheme = mode;
    try {
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) {
            meta.setAttribute('content', mode === THEMES.DARK ? '#0b1020' : '#fbeaec');
        }
    } catch {
        // ignore
    }
    return mode;
}

/**
 * Persist preference and apply the resolved mode. Returns { preference, resolved }.
 */
export function setThemePreference(preference) {
    const pref = VALID_PREFERENCES.has(preference) ? preference : THEMES.LIGHT;
    const allowed = canUseDarkMode() ? pref : THEMES.LIGHT;
    try {
        localStorage.setItem(THEME_STORAGE_KEY, allowed);
    } catch {
        // ignore
    }
    const resolved = resolveTheme(allowed);
    applyTheme(resolved);
    return { preference: allowed, resolved };
}

/** @deprecated use setThemePreference */
export function setTheme(theme) {
    return setThemePreference(theme).resolved;
}

/** Notify ThemeProvider that auth/roles may have changed (same-tab). */
export function notifyThemeAuthChanged() {
    try {
        window.dispatchEvent(new Event('spara-theme-auth'));
    } catch {
        // ignore
    }
}
